import React, { useState } from 'react';
import { ArrowLeft, FileText, Users, Target, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

interface BatterStats {
  name: string;
  runs: number;
  balls: number;
  fours: number;
  sixes: number;
  isOut: boolean;
  dismissal?: string;
  strikeRate: number;
}

interface BowlerStats {
  name: string;
  overs: string;
  maidens: number;
  runs: number;
  wickets: number;
  economy: number;
}

interface InningsData {
  teamName: string;
  runs: number;
  wickets: number;
  overs: string;
  runRate: number;
  batters: BatterStats[];
  bowlers: BowlerStats[];
  extras: {
    byes: number;
    legByes: number;
    wides: number;
    noBalls: number;
    total: number;
  };
  fallOfWickets: Array<{
    wicket: number;
    runs: number;
    batter: string;
    overs: string;
  }>;
}

interface ScorecardProps {
  isOpen: boolean;
  onClose: () => void;
  teamA: string;
  teamB: string;
  innings1?: InningsData;
  innings2?: InningsData;
  currentInnings: 1 | 2;
  matchSettings: {
    oversPerInnings: number;
    numberOfPlayers: number;
    ballsPerOver: number;
  };
  gameComplete: boolean;
  winner?: string;
}

const slideVariants = {
  enter: {
    x: '100%',
    opacity: 0,
  },
  center: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8,
    }
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 40,
      mass: 0.6,
    }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    }
  }
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95 
  },
  show: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
    }
  }
};

export function Scorecard({
  isOpen,
  onClose,
  teamA,
  teamB,
  innings1,
  innings2,
  currentInnings,
  matchSettings,
  gameComplete,
  winner
}: ScorecardProps) {
  const [activeTab, setActiveTab] = useState('summary');

  const formatMatchStatus = () => {
    if (gameComplete && winner) {
      return `${winner} won the match`;
    } else if (currentInnings === 1) {
      return `${teamA} batting - Innings 1`;
    } else {
      return `${teamB} batting - Innings 2`;
    }
  };

  const calculateStrikeRate = (runs: number, balls: number) => {
    return balls > 0 ? ((runs / balls) * 100).toFixed(1) : '0.0';
  };

  const formatOvers = (balls: number, ballsPerOver: number) => {
    const overs = Math.floor(balls / ballsPerOver);
    const remainingBalls = balls % ballsPerOver;
    return remainingBalls > 0 ? `${overs}.${remainingBalls}` : `${overs}`;
  };

  const calculateEconomy = (runs: number, balls: number, ballsPerOver: number) => {
    const overs = balls / ballsPerOver;
    return overs > 0 ? (runs / overs).toFixed(1) : '0.0';
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col bg-white"
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
        >
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between p-4 border-b border-gray-200 bg-white"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <div className="flex items-center gap-3">
              <motion.button
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <ArrowLeft className="w-5 h-5 text-blue-500" />
              </motion.button>
              <div className="flex items-center gap-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                >
                  <FileText className="w-5 h-5 text-blue-500" />
                </motion.div>
                <h1 className="text-lg font-semibold">Scorecard</h1>
              </div>
            </div>
          </motion.div>

          {/* Match Header */}
          <motion.div 
            className="p-4 border-b border-gray-200 bg-gray-50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
          >
            <motion.div 
              className="max-w-4xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <motion.div className="text-center mb-3" variants={itemVariants}>
                <h2 className="text-xl font-semibold">{teamA} vs {teamB}</h2>
                <p className="text-sm text-gray-600">
                  {matchSettings.oversPerInnings} overs • {matchSettings.numberOfPlayers} players
                </p>
              </motion.div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Team A Score */}
                <motion.div variants={itemVariants}>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{teamA}</h3>
                          {innings1 && (
                            <p className="text-2xl font-bold">
                              {innings1.runs}/{innings1.wickets}
                              <span className="text-base font-normal text-gray-600 ml-2">
                                ({innings1.overs} ov)
                              </span>
                            </p>
                          )}
                        </div>
                        {currentInnings === 1 && !gameComplete && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                          >
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Batting
                            </Badge>
                          </motion.div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Team B Score */}
                <motion.div variants={itemVariants}>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{teamB}</h3>
                          {innings2 ? (
                            <p className="text-2xl font-bold">
                              {innings2.runs}/{innings2.wickets}
                              <span className="text-base font-normal text-gray-600 ml-2">
                                ({innings2.overs} ov)
                              </span>
                            </p>
                          ) : currentInnings === 2 ? (
                            <p className="text-2xl font-bold">
                              0/0
                              <span className="text-base font-normal text-gray-600 ml-2">
                                (0.0 ov)
                              </span>
                            </p>
                          ) : (
                            <p className="text-gray-500">Yet to bat</p>
                          )}
                        </div>
                        {currentInnings === 2 && !gameComplete && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                          >
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Batting
                            </Badge>
                          </motion.div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* Match Status */}
              <motion.div 
                className="text-center mt-4"
                variants={itemVariants}
              >
                <Badge variant={gameComplete ? "default" : "secondary"} className="text-sm">
                  {formatMatchStatus()}
                </Badge>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Tabs */}
          <div className="flex-1 overflow-y-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <motion.div 
                className="sticky top-0 bg-white border-b border-gray-200 px-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.3 }}
              >
                <TabsList className="w-full max-w-4xl mx-auto grid grid-cols-3 h-12">
                  <TabsTrigger value="summary" className="text-sm">Summary</TabsTrigger>
                  <TabsTrigger value="batting" className="text-sm">Batting</TabsTrigger>
                  <TabsTrigger value="bowling" className="text-sm">Bowling</TabsTrigger>
                </TabsList>
              </motion.div>

              <motion.div 
                className="p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <div className="max-w-4xl mx-auto">
                  <TabsContent value="summary" className="mt-0">
                    <motion.div 
                      className="space-y-6"
                      variants={containerVariants}
                      initial="hidden"
                      animate="show"
                    >
                      {/* Match Summary */}
                      <motion.div variants={itemVariants}>
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Target className="w-5 h-5" />
                              Match Summary
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {innings1 && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ type: "spring", stiffness: 400 }}
                                >
                                  <p className="text-sm text-gray-600">Total Runs</p>
                                  <p className="text-xl font-bold">{innings1.runs}</p>
                                </motion.div>
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ type: "spring", stiffness: 400 }}
                                >
                                  <p className="text-sm text-gray-600">Wickets</p>
                                  <p className="text-xl font-bold">{innings1.wickets}</p>
                                </motion.div>
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ type: "spring", stiffness: 400 }}
                                >
                                  <p className="text-sm text-gray-600">Run Rate</p>
                                  <p className="text-xl font-bold">{innings1.runRate}</p>
                                </motion.div>
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ type: "spring", stiffness: 400 }}
                                >
                                  <p className="text-sm text-gray-600">Overs</p>
                                  <p className="text-xl font-bold">{innings1.overs}</p>
                                </motion.div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>

                      {/* Fall of Wickets */}
                      {innings1?.fallOfWickets && innings1.fallOfWickets.length > 0 && (
                        <motion.div variants={itemVariants}>
                          <Card>
                            <CardHeader>
                              <CardTitle>Fall of Wickets</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <motion.div 
                                className="grid grid-cols-1 md:grid-cols-2 gap-2"
                                variants={containerVariants}
                                initial="hidden"
                                animate="show"
                              >
                                {innings1.fallOfWickets.map((fow, index) => (
                                  <motion.div 
                                    key={index} 
                                    className="flex justify-between p-2 bg-gray-50 rounded"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.02, backgroundColor: "#f9fafb" }}
                                    transition={{ type: "spring", stiffness: 400 }}
                                  >
                                    <span className="font-medium">{fow.wicket}/{fow.runs}</span>
                                    <span className="text-sm text-gray-600">
                                      {fow.batter} ({fow.overs} ov)
                                    </span>
                                  </motion.div>
                                ))}
                              </motion.div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      )}

                      {/* Extras */}
                      {innings1?.extras && (
                        <motion.div variants={itemVariants}>
                          <Card>
                            <CardHeader>
                              <CardTitle>Extras ({innings1.extras.total})</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[
                                  { label: 'Byes', value: innings1.extras.byes },
                                  { label: 'Leg Byes', value: innings1.extras.legByes },
                                  { label: 'Wides', value: innings1.extras.wides },
                                  { label: 'No Balls', value: innings1.extras.noBalls }
                                ].map((extra, index) => (
                                  <motion.div 
                                    key={extra.label}
                                    className="text-center"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 * index }}
                                    whileHover={{ scale: 1.05 }}
                                  >
                                    <p className="text-sm text-gray-600">{extra.label}</p>
                                    <p className="text-lg font-semibold">{extra.value}</p>
                                  </motion.div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      )}
                    </motion.div>
                  </TabsContent>

                  <TabsContent value="batting" className="mt-0">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Batting Statistics
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Batter</TableHead>
                                  <TableHead className="text-right">Runs</TableHead>
                                  <TableHead className="text-right">Balls</TableHead>
                                  <TableHead className="text-right">4s</TableHead>
                                  <TableHead className="text-right">6s</TableHead>
                                  <TableHead className="text-right">SR</TableHead>
                                  <TableHead>Dismissal</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {innings1?.batters.map((batter, index) => (
                                  <motion.tr 
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ backgroundColor: "#f9fafb" }}
                                    className="transition-colors"
                                  >
                                    <TableCell className="font-medium">{batter.name}</TableCell>
                                    <TableCell className="text-right font-semibold">{batter.runs}</TableCell>
                                    <TableCell className="text-right">{batter.balls}</TableCell>
                                    <TableCell className="text-right">{batter.fours}</TableCell>
                                    <TableCell className="text-right">{batter.sixes}</TableCell>
                                    <TableCell className="text-right">{batter.strikeRate}</TableCell>
                                    <TableCell className="text-sm text-gray-600">
                                      {batter.isOut ? batter.dismissal : 'not out'}
                                    </TableCell>
                                  </motion.tr>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </TabsContent>

                  <TabsContent value="bowling" className="mt-0">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Target className="w-5 h-5" />
                            Bowling Statistics
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Bowler</TableHead>
                                  <TableHead className="text-right">Overs</TableHead>
                                  <TableHead className="text-right">Maidens</TableHead>
                                  <TableHead className="text-right">Runs</TableHead>
                                  <TableHead className="text-right">Wickets</TableHead>
                                  <TableHead className="text-right">Economy</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {innings1?.bowlers.map((bowler, index) => (
                                  <motion.tr 
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ backgroundColor: "#f9fafb" }}
                                    className="transition-colors"
                                  >
                                    <TableCell className="font-medium">{bowler.name}</TableCell>
                                    <TableCell className="text-right">{bowler.overs}</TableCell>
                                    <TableCell className="text-right">{bowler.maidens}</TableCell>
                                    <TableCell className="text-right">{bowler.runs}</TableCell>
                                    <TableCell className="text-right font-semibold">{bowler.wickets}</TableCell>
                                    <TableCell className="text-right">{bowler.economy}</TableCell>
                                  </motion.tr>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </TabsContent>
                </div>
              </motion.div>
            </Tabs>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}